import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ScheduleIcon from "@mui/icons-material/Schedule";
// import dayjs from "dayjs";

const ProcessInquiry = () => {
  // -------------------- STATES --------------------
  const [search, setSearch] = useState("");
  const [processName, setProcessName] = useState("");
  const [processId, setProcessId] = useState("");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [snackbar, setSnackbar] = useState(false);

  // -------------------- MOCK PROCESS LIST --------------------
  const processes = [
    { id: 1, name: "Import Collections", code: "PRC_1001" },
    { id: 2, name: "Reconciliation", code: "PRC_1002" }
  ];

  // -------------------- STATUS CHIP --------------------
  const getStatusChip = (status) => {
    const colors = {
      RUNNING: "primary",
      PAUSED: "warning",
      PENDING: "info",
      COMPLETED: "success",
      FAILED: "error"
    };
    return <Chip label={status} color={colors[status]} size="small" />;
  };

  // -------------------- TABLE COLUMNS --------------------
  const columns = [
    { field: "processId", headerName: "Process ID", width: 130 },
    { field: "processName", headerName: "Process Name", width: 200 },
    { field: "user", headerName: "User", width: 130 },
    { field: "date", headerName: "Date", width: 160 },
    { field: "jobName", headerName: "Job Name", width: 200 },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => getStatusChip(params.value)
    },
    { field: "parameters", headerName: "Parameters", width: 200 }
  ];

  // -------------------- RUN NOW --------------------
  const handleRunNow = () => {
    if (!processId) return;

    setIsRunning(true);

    const newJob = {
      id: Date.now(),
      processId,
      processName,
      user: "Admin",
    //   date: dayjs().format("YYYY-MM-DD HH:mm"),
      jobName: `${processId}_${Date.now()}`,
      status: "PENDING",
      parameters: "CRNS1, CRNS2"
    };

    setJobs((prev) => [newJob, ...prev]);
    setSnackbar(true);

    // Simulate backend status change
    setTimeout(() => {
      setJobs((prev) =>
        prev.map((job) =>
          job.id === newJob.id
            ? { ...job, status: "RUNNING" }
            : job
        )
      );
    }, 2000);

    setTimeout(() => {
      setJobs((prev) =>
        prev.map((job) =>
          job.id === newJob.id
            ? { ...job, status: "COMPLETED" }
            : job
        )
      );
      setIsRunning(false);
    }, 6000);
  };

  // -------------------- SCHEDULE --------------------
  const handleSchedule = () => {
    if (!scheduleDate) return;

    const newJob = {
      id: Date.now(),
      processId,
      processName,
      user: "Admin",
    //   date: dayjs(scheduleDate).format("YYYY-MM-DD HH:mm"),
      jobName: `${processId}_SCH_${Date.now()}`,
      status: "PENDING",
      parameters: "CRNS1, CRNS2"
    };

    setJobs((prev) => [newJob, ...prev]);
    setScheduleOpen(false);
    setSnackbar(true);
  };

  // -------------------- SEARCH FILTER --------------------
  const filteredJobs = jobs.filter(
    (job) =>
      job.processName.toLowerCase().includes(search.toLowerCase()) ||
      job.processId.toLowerCase().includes(search.toLowerCase()) ||
      job.jobName.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box p={3}>
      <Typography variant="h5" mb={3}>
       Search Process
      </Typography>

      {/* -------------------- LOV SECTION -------------------- */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Process Name"
              value={processName}
              onChange={(e) => {
                const selected = processes.find(
                  (p) => p.name === e.target.value
                );
                setProcessName(selected.name);
                setProcessId(selected.code);
              }}
            >
              {processes.map((p) => (
                <MenuItem key={p.id} value={p.name}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              fullWidth
              label="Process ID"
              value={processId}
              onChange={(e) => {
                const selected = processes.find(
                  (p) => p.code === e.target.value
                );
                setProcessId(selected.code);
                setProcessName(selected.name);
              }}
            >
              {processes.map((p) => (
                <MenuItem key={p.id} value={p.code}>
                  {p.code}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* -------------------- EXECUTION PANEL -------------------- */}
      {processId && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6">Process Execution</Typography>
          <Typography>Name: {processName}</Typography>
          <Typography mb={2}>ID: {processId}</Typography>

          <Grid container spacing={2} mb={2}>
            <Grid item>
              <Button variant="outlined" startIcon={<UploadFileIcon />}>
                Import CRNS - 1
              </Button>
            </Grid>
            <Grid item>
              <Button variant="outlined" startIcon={<UploadFileIcon />}>
                Import CRNS - 2
              </Button>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleRunNow}
                disabled={!processId || isRunning}
              >
                {isRunning ? "Running..." : "Run Now"}
              </Button>
            </Grid>

            <Grid item>
              <Button
                variant="outlined"
                startIcon={<ScheduleIcon />}
                onClick={() => setScheduleOpen(true)}
                disabled={!processId}
              >
                Schedule
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* -------------------- HISTORY TABLE -------------------- */}
      <Paper sx={{ height: 450, p: 2 }}>
        <Typography variant="h6" mb={2}>
          Execution History
        </Typography>
        <DataGrid
          rows={filteredJobs}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10]}
        />
      </Paper>

      {/* -------------------- SCHEDULE MODAL -------------------- */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)}>
        <DialogTitle>Schedule Process</DialogTitle>
        <DialogContent>
          <TextField
            type="datetime-local"
            fullWidth
            sx={{ mt: 2 }}
            onChange={(e) => setScheduleDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSchedule}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------------------- SNACKBAR -------------------- */}
      <Snackbar
        open={snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(false)}
      >
        <Alert severity="success" variant="filled">
          Job Submitted Successfully
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProcessInquiry;
